/*
 * Copyright (c) CIRROM 2025.
 */

import {
  StatsForSystemOrProject,
  StatForSystemOrProject,
} from "../../../api/dashboards";
import { getPercentageColor } from "../../../utils/dashboardUtils";

type EntityType = "CONTACT" | "ACCOUNT";
type FieldCategory =
  | "name_fields"
  | "email_fields"
  | "location_fields"
  | "employment_fields";

export interface DashboardStatsItem {
  title: string;
  amount: number;
  trendValue: number;
  percentageText: string;
  percentageColor: string;
  navigationUrl?: string;
}

interface DashboardStatsByEntity {
  contact: DashboardStatsItem[];
  account: DashboardStatsItem[];
}

interface StatCategoryConfig {
  id: string;
  label: string;
  fieldCategories: FieldCategory[];
  supportedEntities: EntityType[];
}

const STAT_CATEGORY_CONFIG: StatCategoryConfig[] = [
  {
    label: "Name",
    id: "name",
    fieldCategories: ["name_fields"],
    supportedEntities: ["CONTACT"],
  },

  {
    label: "Email",
    id: "email",
    fieldCategories: ["email_fields"],
    supportedEntities: ["CONTACT"],
  },
  {
    label: "Location",
    id: "location",
    fieldCategories: ["location_fields"],
    supportedEntities: ["CONTACT", "ACCOUNT"],
  },
  {
    id: "Employment",
    label: "Employment",
    fieldCategories: ["employment_fields"],
    supportedEntities: ["CONTACT"],
  },
];

// Default values for placeholder data
const DEFAULT_STAT_PLACEHOLDERS = {
  trendValue: 0,
  percentageText: "0",
  percentageColor: getPercentageColor(0),
} as const;

/**
 * Calculates the total count for a specific entity type across field categories
 */
const calculateEntityTotal = (
  stats: StatsForSystemOrProject,
  fieldCategories: FieldCategory[],
  entityType: EntityType
): number => {
  return fieldCategories.reduce((total, category) => {
    const categoryFields = stats[category];
    if (!categoryFields) return total;

    return (
      total +
      Object.values(categoryFields).reduce(
        (categoryTotal, field: StatForSystemOrProject) =>
          categoryTotal + (field[entityType] || 0),
        0
      )
    );
  }, 0);
};

/**
 * Calculates the total delta for a specific entity type across field categories
 */
const calculateEntityDelta = (
  stats: StatsForSystemOrProject,
  fieldCategories: FieldCategory[],
  entityType: EntityType
): number => {
  return fieldCategories.reduce((total, category) => {
    const categoryFields = stats[category];
    if (!categoryFields) return total;

    return (
      total +
      Object.values(categoryFields).reduce(
        (categoryTotal, field: StatForSystemOrProject) =>
          categoryTotal + (field.delta?.[entityType] || 0),
        0
      )
    );
  }, 0);
};

/**
 * Calculates percentage change and determines color based on the change
 */
const calculatePercentageChange = (
  current: number,
  delta: number
): { percentageText: string; percentageColor: string; trendValue: number } => {
  if (current === 0 && delta === 0) {
    return {
      percentageText: "0%",
      percentageColor: getPercentageColor(0),
      trendValue: 0,
    };
  }

  const previousValue = current - delta;
  if (previousValue === 0) {
    // If previous was 0 and current > 0, it's infinite growth
    const infinitePercentage = delta > 0 ? 100 : 0;
    return {
      percentageText: delta > 0 ? "∞%" : "0%",
      percentageColor: getPercentageColor(infinitePercentage),
      trendValue: delta,
    };
  }

  const percentage = Math.round((delta / previousValue) * 100);

  return {
    percentageText: `${percentage > 0 ? "+" : ""}${percentage}%`,
    percentageColor: getPercentageColor(percentage),
    trendValue: delta,
  };
};

/**
 * Creates stats for a specific entity type
 */
const createEntityStats = (
  stats: StatsForSystemOrProject,
  entityType: EntityType,
  integratedSystemId: string,
  isDataProject: boolean
): DashboardStatsItem[] => {
  const navigationBaseLine = isDataProject ? "/data-project/" : "/product/";

  const entityStats = STAT_CATEGORY_CONFIG.filter((config) =>
    config.supportedEntities.includes(entityType)
  ).map((config) => {
    const amount = calculateEntityTotal(
      stats,
      config.fieldCategories,
      entityType
    );
    const delta = calculateEntityDelta(
      stats,
      config.fieldCategories,
      entityType
    );
    const percentageData = calculatePercentageChange(amount, delta);

    return {
      title: config.label,
      amount,
      navigationUrl: `${navigationBaseLine}all-data/${entityType.toLowerCase()}/${config.id.toLowerCase()}/${integratedSystemId}`,
      ...percentageData,
    };
  });

  entityStats.push({
    title: "Duplicates",
    amount:
      entityType.toLowerCase() === "contact"
        ? stats.duplicates?.total
        : stats.account_duplicates?.total,
    navigationUrl: `${navigationBaseLine}${entityType.toLowerCase()}/duplicates/${integratedSystemId}`,
    ...DEFAULT_STAT_PLACEHOLDERS,
  });

  if (entityType.toLowerCase() === "account") {
    entityStats.push({
      title: "Address Duplicates",
      amount: stats.address_duplicates?.total,
      navigationUrl: `${navigationBaseLine}${entityType.toLowerCase()}/address/${integratedSystemId}`,
      ...DEFAULT_STAT_PLACEHOLDERS,
    });
  }

  return entityStats;
};

/**
 * Hook that transforms system/project stats into dashboard stats split by entity type
 * @param stats - The raw statistics from the API
 * @param integratedSystemId
 * @param isDataProject
 * @returns Object containing contact and account dashboard stats arrays
 */
export const useDashboardStatsSplitByEntity = (
  stats: StatsForSystemOrProject | undefined,
  integratedSystemId: string,
  isDataProject: boolean
): DashboardStatsByEntity => {
  // Early return with empty arrays if no stats provided
  if (!stats) {
    return { contact: [], account: [] };
  }

  return {
    contact: createEntityStats(
      stats,
      "CONTACT",
      integratedSystemId,
      isDataProject
    ),
    account: createEntityStats(
      stats,
      "ACCOUNT",
      integratedSystemId,
      isDataProject
    ),
  };
};
